import { getImageWithBlackFrameElements } from '../../utility/elements';
import { CompositionElement } from '../models/elements/CompositionElement';
import { ImageElement } from '../models/elements/ImageElement';
import { Timeline } from '../models/Timeline';
import { EditorChangeType } from '../types';
import { AbstractCommand } from './AbstractCommand';

import type { Element } from '../models/Element';
import type { VideoEditor } from '../VideoEditor';

/**
 * Command for transitioning elements between different types (ImageElement ↔ CompositionElement)
 * This handles complex transitions like image fit changes that require type changes
 */
export class ElementTypeTransitionCommand extends AbstractCommand {
  private oldElement: Element | null = null;

  getName(): string {
    return 'ElementTypeTransitionCommand';
  }
  private newElement: Element | null = null;
  private timeline: Timeline;
  private elementId: string;
  private targetType: 'image' | 'composition';
  private properties: Record<string, any>;

  constructor(
    editor: VideoEditor,
    elementId: string,
    targetType: 'image' | 'composition',
    properties: Record<string, any>,
  ) {
    super(editor);
    this.timeline = editor.timeline;
    this.elementId = elementId;
    this.targetType = targetType;
    this.properties = properties;
  }

  execute(): boolean {
    try {
      // Find the existing element
      this.oldElement = this.timeline.getElement(this.elementId) || null;
      if (!this.oldElement) {
        console.error(`Element with ID ${this.elementId} not found`);
        return false;
      }

      // Create new element based on target type
      this.newElement = this.createTransitionedElement(
        this.oldElement,
        this.targetType,
        this.properties,
      );
      if (!this.newElement) {
        console.error(
          `Failed to create transitioned element of type ${this.targetType}`,
        );
        return false;
      }

      // Replace element in timeline
      const success = this.timeline.replaceElement(
        this.elementId,
        this.newElement,
      );
      if (!success) {
        console.error(
          `Failed to replace element ${this.elementId} in timeline`,
        );
        return false;
      }

      // Notify observers of the change
      this.editor.notifyObservers(EditorChangeType.ELEMENT_UPDATED, {
        elementId: this.elementId,
        properties: this.properties,
        oldElement: this.oldElement,
        newElement: this.newElement,
      });

      return true;
    } catch (error) {
      console.error('Error executing ElementTypeTransitionCommand:', error);
      return false;
    }
  }

  undo(): boolean {
    try {
      if (!this.oldElement) {
        console.error('Cannot undo: old element not found');
        return false;
      }

      // Restore the original element
      const success = this.timeline.replaceElement(
        this.elementId,
        this.oldElement,
      );
      if (!success) {
        console.error(
          `Failed to restore element ${this.elementId} in timeline`,
        );
        return false;
      }

      // Notify observers of the revert
      this.editor.notifyObservers(EditorChangeType.ELEMENT_UPDATED, {
        elementId: this.elementId,
        properties: {},
        oldElement: this.newElement,
        newElement: this.oldElement,
      });

      return true;
    } catch (error) {
      console.error('Error undoing ElementTypeTransitionCommand:', error);
      return false;
    }
  }

  /**
   * Creates a new element of the target type with properties mapped from the old element
   */
  private createTransitionedElement(
    oldElement: Element,
    targetType: 'image' | 'composition',
    properties: Record<string, any>,
  ): Element | null {
    // For same-type updates, preserve everything and only apply the specific changes
    if (this.isSameTypeUpdate(oldElement, targetType)) {
      return this.updateExistingElement(oldElement, properties);
    }

    const baseProperties = this.extractBaseProperties(oldElement);

    if (targetType === 'composition') {
      return this.createCompositionFromImage(
        oldElement,
        baseProperties,
        properties,
      );
    } else if (targetType === 'image') {
      return this.createImageFromComposition(
        oldElement,
        baseProperties,
        properties,
      );
    }

    return null;
  }

  /**
   * Checks if this is a same-type update (no actual type transition needed)
   */
  private isSameTypeUpdate(
    element: Element,
    targetType: 'image' | 'composition',
  ): boolean {
    const isCurrentComposition = element instanceof CompositionElement;
    const isCurrentImage = element instanceof ImageElement;

    return (
      (targetType === 'composition' && isCurrentComposition) ||
      (targetType === 'image' && isCurrentImage)
    );
  }

  /**
   * Updates an existing element while preserving its structure
   */
  private updateExistingElement(
    oldElement: Element,
    properties: Record<string, any>,
  ): Element | null {
    try {
      // Clone the element to avoid modifying the original
      const clonedElement = this.deepCloneElement(oldElement);

      // For composition elements, apply properties to the correct child elements
      if (clonedElement instanceof CompositionElement) {
        return this.updateCompositionElement(clonedElement, properties);
      } else {
        // For other elements, apply properties directly
        clonedElement.update(properties);
        return clonedElement;
      }
    } catch (error) {
      console.error('Error updating existing element:', error);
      return null;
    }
  }

  /**
   * Deep clone an element preserving all properties
   */
  private deepCloneElement(element: Element): Element {
    if (element instanceof CompositionElement) {
      // For compositions, we need to preserve child elements
      const clonedChildren = element.elements.map(child =>
        this.deepCloneElement(child),
      );
      const props = {
        ...element.toJSON(),
        elements: clonedChildren,
      };
      return new CompositionElement(props);
    } else if (element instanceof ImageElement) {
      return new ImageElement(element.toJSON());
    } else {
      // For other element types, use the Timeline.createElement
      return Timeline.createElement(element.type, element.toJSON());
    }
  }

  /**
   * Updates a composition element while preserving its structure
   */
  private updateCompositionElement(
    composition: CompositionElement,
    properties: Record<string, any>,
  ): CompositionElement {
    // Apply composition-level properties
    const compositionProps = this.filterCompositionProperties(properties);
    if (Object.keys(compositionProps).length > 0) {
      composition.update(compositionProps);
    }

    // Apply image-specific properties to the image child element
    const imageProps = this.filterImageProperties(properties);
    if (Object.keys(imageProps).length > 0) {
      const imageChild = composition.elements.find(
        el => el instanceof ImageElement,
      ) as ImageElement;
      if (imageChild) {
        // All properties are now direct properties since metadata system was removed
        imageChild.update(imageProps);
      }
    }

    return composition;
  }

  /**
   * Filters properties that should be applied to the composition itself
   */
  private filterCompositionProperties(
    properties: Record<string, any>,
  ): Record<string, any> {
    const compositionProps: Record<string, any> = {};

    // Properties that belong to the composition
    const compositionKeys = [
      'time',
      'duration',
      'track',
      'x',
      'y',
      'width',
      'height',
      'visible',
      'locked',
      'dynamic',
      'animations',
      'z_index',
    ];

    for (const key of compositionKeys) {
      if (key in properties) {
        compositionProps[key] = properties[key];
      }
    }

    return compositionProps;
  }

  /**
   * Filters properties that should be applied to the image child element
   */
  private filterImageProperties(
    properties: Record<string, any>,
  ): Record<string, any> {
    const imageProps: Record<string, any> = {};

    // Properties that belong to the image child (all are now direct properties)
    const imageKeys = [
      'source',
      'fit',
      'opacity',
      'color_overlay',
      'smart_crop',
    ];

    for (const key of imageKeys) {
      if (key in properties) {
        imageProps[key] = properties[key];
      }
    }

    return imageProps;
  }

  /**
   * Extracts common properties that should be preserved across type transitions
   */
  private extractBaseProperties(element: Element): Record<string, any> {
    const baseProps: Record<string, any> = {
      id: element.id,
      track: element.track,
      time: element.time,
      duration: element.duration,
      visible: element.visible,
      locked: element.locked,
      dynamic: element.dynamic,
      x: element.x,
      y: element.y,
      width: element.width,
      height: element.height,
    };

    // Preserve animations if they exist
    if (element.animations) {
      baseProps.animations = element.animations;
    }

    // Preserve z_index as a direct property (metadata system removed)
    if (element.z_index !== undefined) {
      baseProps.z_index = element.z_index;
    }

    // Preserve opacity as a direct property (metadata system removed)
    if (element.opacity !== undefined) {
      baseProps.opacity = element.opacity;
    }

    // Preserve other visual effects that might be applied
    if (element.blend_mode !== undefined && element.blend_mode !== 'none') {
      baseProps.blend_mode = element.blend_mode;
    }

    if (element.color_overlay !== undefined && element.color_overlay !== null) {
      baseProps.color_overlay = element.color_overlay;
    }

    // For composition elements, preserve existing child element properties
    if (element instanceof CompositionElement) {
      // Extract properties from existing child elements
      const imageChild = element.elements.find(
        el => el instanceof ImageElement,
      ) as ImageElement;
      const shapeChild = element.elements.find(el => el.type === 'shape');

      if (imageChild) {
        baseProps.imageSource = imageChild.source;
        baseProps.imageFit = imageChild.fit || 'contain';
        baseProps.imageColorOverlay = imageChild.color_overlay || '';
        baseProps.imageZIndex = imageChild.z_index;
      }

      if (shapeChild) {
        baseProps.shapeFillColor = shapeChild.fill_color || '#000';
        baseProps.shapeZIndex = shapeChild.z_index;
      }
    }

    return baseProps;
  }

  /**
   * Creates a CompositionElement from an ImageElement (for black-frame fit)
   */
  private createCompositionFromImage(
    oldElement: Element,
    baseProperties: Record<string, any>,
    properties: Record<string, any>,
  ): CompositionElement | null {
    try {
      // Extract source from old element
      let source: string = '';
      if (oldElement instanceof ImageElement && oldElement.source) {
        source = oldElement.source;
      } else if (baseProperties.imageSource) {
        source = baseProperties.imageSource;
      } else {
        console.error(
          'Cannot extract source from old element for composition transition',
        );
        return null;
      }

      // Create child elements with preserved properties
      const childElementsData = getImageWithBlackFrameElements({
        source,
        fit: baseProperties.imageFit || 'contain',
        color_overlay: baseProperties.imageColorOverlay || '',
      });

      // Enhance child elements with ALL preserved properties
      if (childElementsData[0]) {
        // Shape element (index 0)
        if (baseProperties.shapeFillColor) {
          childElementsData[0].fill_color = baseProperties.shapeFillColor;
        }
        if (baseProperties.shapeZIndex !== undefined) {
          childElementsData[0].z_index = baseProperties.shapeZIndex;
        }
      }

      if (childElementsData[1]) {
        // Image element (index 1)
        if (baseProperties.imageColorOverlay !== undefined) {
          childElementsData[1].color_overlay = baseProperties.imageColorOverlay;
        }
        if (baseProperties.imageZIndex !== undefined) {
          childElementsData[1].z_index = baseProperties.imageZIndex;
        }
      }

      const childElements = childElementsData.map(elementData =>
        Timeline.createElement(elementData.type as any, elementData),
      );

      // Filter properties for composition level vs child level
      const compositionLevelProps =
        this.filterCompositionProperties(properties);

      // Create clean base properties without image-specific props
      const cleanBaseProps = { ...baseProperties };
      delete cleanBaseProps.imageSource;
      delete cleanBaseProps.imageFit;
      delete cleanBaseProps.imageColorOverlay;
      delete cleanBaseProps.shapeFillColor;

      // Create the composition element
      const compositionProps = {
        ...cleanBaseProps,
        type: 'composition',
        source: null, // Compositions don't have direct sources
        elements: childElements,
        flow_direction: 'vertical',
        loop: false,
        plays: null,
        // Ensure track is preserved from original element
        track: this.oldElement?.track || baseProperties.track,
        // Only apply composition-level properties
        ...compositionLevelProps,
      };

      const composition = new CompositionElement(compositionProps);

      // Apply image-specific properties to the image child
      const imageProps = this.filterImageProperties(properties);
      if (Object.keys(imageProps).length > 0) {
        const imageChild = composition.elements.find(
          el => el instanceof ImageElement,
        ) as ImageElement;
        if (imageChild) {
          // All properties are now direct properties since metadata system was removed
          imageChild.update(imageProps);
        }
      }

      return composition;
    } catch (error) {
      console.error('Error creating composition from image:', error);
      return null;
    }
  }

  /**
   * Creates an ImageElement from a CompositionElement (for direct fit)
   */
  private createImageFromComposition(
    oldElement: Element,
    baseProperties: Record<string, any>,
    properties: Record<string, any>,
  ): ImageElement | null {
    try {
      // Extract source from composition's child elements
      let source: string = '';

      if (oldElement instanceof CompositionElement) {
        const imageChild = oldElement.elements.find(
          el => el instanceof ImageElement,
        ) as ImageElement;
        if (imageChild) {
          source = imageChild.source;
        }
      }

      if (!source) {
        console.error(
          'Cannot extract source from composition for image transition',
        );
        return null;
      }

      // Get the track number for z_index alignment
      const trackNumber = this.oldElement?.track || baseProperties.track;

      // Create the image element
      const imageProps = {
        ...baseProperties,
        type: 'image',
        source,
        fit: properties.fit || 'cover',
        smart_crop: true,
        autoplay: true,
        // Ensure track is preserved from original element
        track: trackNumber,
        // Force z_index to match track number when transitioning from composition to image
        z_index: trackNumber,
        // Apply any additional properties
        ...properties,
      };

      return new ImageElement(imageProps);
    } catch (error) {
      console.error('Error creating image from composition:', error);
      return null;
    }
  }

  /**
   * Gets a description of this command for debugging/logging
   */
  getDescription(): string {
    return `Transition element ${this.elementId} to type ${this.targetType}`;
  }
}
